import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';

/**
 * Expenses, their categories, and the vendors an invoiced expense is billed
 * to — the phone's `lib/services/network_service.dart`, brought across call
 * for call.
 *
 * Three collections sit behind one screen because they are one job: money
 * going out, what kind of money it was, and who is owed it.
 *
 * Roles: `/expenses` is restricted to manager, cashier and accountant, so a
 * waiter reaching this screen would get a 403 rather than a list. Waiters are
 * already held on the order pad by the layout, which is the real guard.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/** The API resolves each bound to a whole calendar day, so time is not sent. */
export const API_DATE = 'YYYY-MM-DD';

/* ── expenses ───────────────────────────────────────────────────────────── */

/**
 * Everything spent in a window, newest first.
 *
 * The server excludes canceled expenses and populates both `vendor` and
 * `expenseType`, so a row arrives with its category name already on it — the
 * one place that is NOT true is the response to an update, see below.
 */
export async function fetchExpenses({ businessId, start, end, token, signal }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/expenses/get-business-expenses/${businessId}` +
      `?startDate=${start}&endDate=${end}`,
    { ...auth(token), cancelToken: signal }
  );
  return data?.data?.expenses || [];
}

export async function createExpense({ payload, token }) {
  const { data } = await axios.post(`${constants.baseUrl}/expenses`, payload, auth(token));
  return data?.data?.createdExpense;
}

/**
 * Save an edit.
 *
 * The server saves the document and returns it WITHOUT populating
 * `expenseType`, so what comes back carries a category id where the list has
 * a category object. The caller re-attaches the category it already holds
 * rather than refetching the whole period for one row.
 */
export async function updateExpense({ id, payload, token }) {
  const { data } = await axios.patch(
    `${constants.baseUrl}/expenses/${id}`,
    payload,
    auth(token)
  );
  return data?.data?.updatedExpense;
}

/** A hard delete — 204, no body. An invoiced expense takes its transaction. */
export async function deleteExpense({ id, token }) {
  await axios.delete(`${constants.baseUrl}/expenses/${id}`, auth(token));
}

/* ── categories ─────────────────────────────────────────────────────────── */

/**
 * The phone calls these expense categories; the API calls them expense types.
 * The API's word is used for the endpoints and the payload field, and the
 * phone's word everywhere a person can read it.
 */
export async function fetchExpenseTypes({ businessId, token }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/expense-types/get-business-expense-types/${businessId}`,
    auth(token)
  );
  return data?.data?.expenseTypes || [];
}

export async function createExpenseType({ payload, token }) {
  const { data } = await axios.post(
    `${constants.baseUrl}/expense-types`,
    payload,
    auth(token)
  );
  return data?.data?.createdExpenseType;
}

export async function updateExpenseType({ id, payload, token }) {
  const { data } = await axios.patch(
    `${constants.baseUrl}/expense-types/${id}`,
    payload,
    auth(token)
  );
  return data?.data?.updatedExpenseType;
}

export async function deleteExpenseType({ id, token }) {
  await axios.delete(`${constants.baseUrl}/expense-types/${id}`, auth(token));
}

/* ── vendors ────────────────────────────────────────────────────────────── */

/**
 * Who an invoiced expense is owed to.
 *
 * Fetched only when somebody actually picks Invoice — most expenses are cash
 * and the list can be long. Paying a vendor down is the Vendors screen's job
 * on the phone and is not part of this one.
 */
export async function fetchVendors({ businessId, token }) {
  const { data } = await axios.get(
    `${constants.baseUrl}/vendors/get-business-vendors/${businessId}`,
    auth(token)
  );
  return data?.data?.vendors || [];
}
